import { useMemo, useRef, useState } from "react";

import { Button, ModalWrapper } from "../ui";
import {
  getUsersByRole,
  startImpersonation,
  stopImpersonation,
} from "../../services/api/core";
import useOutsideClick from "../../hooks/useOutsideClick";
import { openAdminPanel } from "../../utils/common";
import { getUserDisplayName, getUserInitials } from "../../utils/business";

/**
 * Executive user menu for admin access, impersonation, and logout.
 *
 * @param {Object} props Component props.
 * @param {Object|null} props.user Effective current user.
 * @param {Object} props.impersonation Current impersonation metadata.
 * @param {string|null} props.baseURL Backend base URL.
 * @param {Function} props.refreshUser Refreshes CoreContext user metadata.
 * @param {Function} props.onLogout Opens logout confirmation.
 * @returns {JSX.Element} Executive user portal.
 */
const UserPortal = ({ user, impersonation, baseURL, refreshUser, onLogout }) => {
  const portalRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isImpersonationModalOpen, setIsImpersonationModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [error, setError] = useState("");
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isImpersonating = Boolean(impersonation?.active);
  const controllingUser = impersonation?.real_user || user;

  useOutsideClick(portalRef, () => setIsMenuOpen(false));

  const availableUsers = useMemo(() => {
    const query = search.toLowerCase();
    return users
      .filter((candidate) => !candidate.executive)
      .filter((candidate) => candidate.id !== controllingUser?.id)
      .filter((candidate) => {
        const searchableValue = [
          candidate.first,
          candidate.last,
          candidate.email,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return searchableValue.includes(query);
      });
  }, [controllingUser?.id, search, users]);

  /**
   * Opens the Django admin panel in a new browser tab.
   *
   * @returns {void}
   */
  const handleOpenAdmin = () => {
    openAdminPanel(baseURL);
    setIsMenuOpen(false);
  };

  /**
   * Loads users and opens the impersonation picker.
   *
   * @returns {Promise<void>}
   */
  const handleOpenImpersonationModal = async () => {
    setIsMenuOpen(false);
    setIsImpersonationModalOpen(true);
    setSearch("");
    setSelectedUserId("");
    setError("");
    setIsLoadingUsers(true);

    try {
      const response = await getUsersByRole({ active: true });
      setUsers(response || []);
    } catch (loadError) {
      setError("Unable to load users for impersonation.");
    } finally {
      setIsLoadingUsers(false);
    }
  };

  /**
   * Starts impersonation for the selected user.
   *
   * @returns {Promise<void>}
   */
  const handleConfirmImpersonation = async () => {
    if (!selectedUserId) {
      setError("Select a user to impersonate.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await startImpersonation(Number(selectedUserId));
      await refreshUser();
      setIsImpersonationModalOpen(false);
    } catch (submitError) {
      setError(
        submitError.response?.data?.message || "Unable to start impersonation."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Ends the current impersonation session.
   *
   * @returns {Promise<void>}
   */
  const handleEndImpersonation = async () => {
    setIsSubmitting(true);
    setError("");

    try {
      await stopImpersonation();
      await refreshUser();
      setIsMenuOpen(false);
    } catch (submitError) {
      setError(
        submitError.response?.data?.message || "Unable to end impersonation."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative" ref={portalRef}>
      <button
        className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 text-sm font-bold text-white transition hover:bg-white/20"
        type="button"
        onClick={() => setIsMenuOpen((isOpen) => !isOpen)}
        title="User menu"
      >
        {getUserInitials(controllingUser)}
      </button>

      {isMenuOpen ? (
        <div className="absolute right-0 top-12 z-[60] w-64 overflow-hidden rounded-lg border border-gray-200 bg-white py-2 text-left text-sm text-gray-700 shadow-xl">
          <div className="border-b border-gray-100 px-4 py-3">
            <p className="font-semibold text-gray-900">
              {getUserDisplayName(controllingUser)}
            </p>
            <p className="truncate text-xs text-gray-500">
              {controllingUser?.email}
            </p>
          </div>

          {error ? (
            <p className="mx-3 mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </p>
          ) : null}

          <button
            className="block w-full px-4 py-2 text-left hover:bg-gray-50"
            type="button"
            onClick={handleOpenAdmin}
          >
            Open Admin Panel
          </button>

          {isImpersonating ? (
            <button
              className="mx-3 my-2 block w-[calc(100%-1.5rem)] rounded-md bg-orange-800 px-3 py-2 text-left font-semibold text-white hover:bg-orange-700"
              type="button"
              onClick={handleEndImpersonation}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Ending..." : "End Impersonation"}
            </button>
          ) : (
            <button
              className="block w-full px-4 py-2 text-left hover:bg-gray-50"
              type="button"
              onClick={handleOpenImpersonationModal}
            >
              Begin User Impersonation
            </button>
          )}

          <div className="mt-2 border-t border-gray-100 pt-2">
            <button
              className="block w-full px-4 py-2 text-left text-red-700 hover:bg-red-50"
              type="button"
              onClick={() => {
                setIsMenuOpen(false);
                onLogout();
              }}
            >
              Logout
            </button>
          </div>
        </div>
      ) : null}

      <ModalWrapper
        isOpen={isImpersonationModalOpen}
        onClose={() => setIsImpersonationModalOpen(false)}
        maxWidth={560}
      >
        <div className="p-6">
          <h2 className="pr-8 text-xl font-bold text-gray-900">
            Begin User Impersonation
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Select an active non-executive user to act on their behalf.
          </p>

          <label className="mt-6 block text-sm font-semibold text-gray-700">
            Search Users
            <input
              className="mt-2 block min-h-[42px] w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-secondary-400 focus:ring-4 focus:ring-secondary-100"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name or email"
            />
          </label>

          <div className="mt-4 max-h-72 overflow-y-auto rounded-md border border-gray-200 min-h-[288px]">
            {isLoadingUsers ? (
              <p className="px-4 py-6 text-center text-sm text-gray-500">
                Loading users...
              </p>
            ) : availableUsers.length ? (
              availableUsers.map((candidate) => (
                <label
                  key={candidate.id}
                  className="flex cursor-pointer items-start gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0 hover:bg-gray-50"
                >
                  <input
                    className="mt-1"
                    type="radio"
                    name="impersonation_user"
                    value={candidate.id}
                    checked={Number(selectedUserId) === candidate.id}
                    onChange={(event) => setSelectedUserId(event.target.value)}
                  />
                  <span>
                    <span className="block font-semibold text-gray-900">
                      {getUserDisplayName(candidate)}
                    </span>
                    <span className="block text-xs text-gray-500">
                      {candidate.email}
                    </span>
                  </span>
                </label>
              ))
            ) : (
              <p className="px-4 py-6 text-center text-sm text-gray-500">
                No matching users found.
              </p>
            )}
          </div>

          {error ? (
            <p className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          ) : null}

          <div className="mt-6 flex justify-end gap-3">
            <Button
              variant="cancel"
              text="Cancel"
              onClick={() => setIsImpersonationModalOpen(false)}
            />
            <Button
              variant={isSubmitting || !selectedUserId ? "disabled" : "primary"}
              text={isSubmitting ? "Starting..." : "Confirm"}
              onClick={handleConfirmImpersonation}
            />
          </div>
        </div>
      </ModalWrapper>
    </div>
  );
};

export default UserPortal;
